import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMatch, useNavigate } from 'react-router'
import { Box, Icon, Paragraph, Stack } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useCreateLink } from 'hooks/useCreateLink'
import { useDevice } from 'hooks/useDevice'
import { useShortcut } from 'hooks/useShortcut'
import {
    DmAndGdmModel,
    DMChannelContextUserLookupProvider,
    useDmsAndGdms,
    useDmsAndGdmsUnreads,
} from 'use-towns-client'

import { DirectMessageListItem } from './DirectMessageListItem'

export const DirectMessageList = () => {
    const { channels: dmChannelIds } = useFilteredDirectMessages()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()

    const routeMatch = useMatch('messages/:channelId/*')

    const channelId = routeMatch?.params?.channelId

    const { dmsAndGdmsUnreadIds } = useDmsAndGdmsUnreads()

    const { selectMessage } = useSelectMessage(dmChannelIds, channelId)

    const searchRef = useRef<HTMLInputElement>(null)

    useShortcut('DisplaySearchModal', () => {
        searchRef.current?.focus()
    })

    return (
        <>
            <Stack minHeight="100svh" paddingBottom="safeAreaInsetBottom" gap="sm">
                {dmChannelIds.length > 0 ? (
                    dmChannelIds.map((channel) => {
                        return (
                            <DMChannelContextUserLookupProvider
                                key={channel.id}
                                channelId={channel.id}
                                abstractAccountAddress={abstractAccountAddress}
                            >
                                <DirectMessageListItem
                                    key={channel.id}
                                    channel={channel}
                                    selected={channelId === channel.id}
                                    unread={dmsAndGdmsUnreadIds.has(channel.id)}
                                    onSelect={selectMessage}
                                />
                            </DMChannelContextUserLookupProvider>
                        )
                    })
                ) : (
                    <Box centerContent absoluteFill padding="md" pointerEvents="none">
                        <Paragraph as="span" color="gray2" textAlign="center">
                            Click the compose button above{' '}
                            <Box
                                display="inline-block"
                                style={{ verticalAlign: 'middle' }}
                                paddingBottom="xxs"
                            >
                                <Icon type="compose" size="square_sm" />
                            </Box>
                            <br />
                            to write your first message
                        </Paragraph>
                    </Box>
                )}
            </Stack>
        </>
    )
}

const useFilteredDirectMessages = () => {
    const { dmsAndGdms } = useDmsAndGdms()
    const channels = useMemo(() => dmsAndGdms.filter((c) => !c.left), [dmsAndGdms])
    return { channels }
}

const useSelectMessage = (dmChannels: DmAndGdmModel[], messageId?: string) => {
    const { isTouch } = useDevice()
    const navigate = useNavigate()
    const hasInitRef = useRef(false)
    const { createLink } = useCreateLink()

    // auto select first message if needed
    useEffect(() => {
        if (!hasInitRef.current && !isTouch && !messageId && dmChannels.length > 0) {
            const link = createLink({ messageId: dmChannels[0].id })
            if (link) {
                navigate(link)
            }
        }
        hasInitRef.current = true
    }, [messageId, dmChannels, navigate, isTouch, createLink])

    const selectMessage = useCallback(
        (id: string) => {
            const link = createLink({ messageId: id })
            if (link) {
                navigate({ pathname: link, search: 'stackId=direct-messages' })
            }
        },
        [createLink, navigate],
    )

    return { selectMessage }
}
